import React, { ComponentPropsWithRef, forwardRef } from "react";
import PropTypes from "prop-types";
import { ReactDefaultPropsWithChildren } from "../../types/react";

import styles from "./Accordion.scss";

export const AccordionItemSublineDefaultTagName = "p";
export type AccordionItemSublineDefaultTag = typeof AccordionItemSublineDefaultTagName;

type AccordionItemSublineBaseProps<
  Tag extends React.ElementType = AccordionItemSublineDefaultTag
> = {
  tag?: Tag;
};
export type AccordionItemSublineProps<
  Tag extends React.ElementType = AccordionItemSublineDefaultTag
> = AccordionItemSublineBaseProps<Tag> &
  Omit<
    React.ComponentPropsWithRef<Tag>,
    keyof AccordionItemSublineBaseProps<Tag>
  >;

const defaultProps = {
  tag: AccordionItemSublineDefaultTagName,
  children: "",
} as const;

const validatedDefaultProps: ReactDefaultPropsWithChildren<
  AccordionItemSublineBaseProps,
  typeof defaultProps
> = defaultProps;

const AccordionSublineWithRef = <
  Tag extends React.ElementType = AccordionItemSublineDefaultTag
>(
  props: AccordionItemSublineProps<Tag>,
  ref: ComponentPropsWithRef<Tag>["ref"]
) => {
  const {
    children = defaultProps.children,
    tag: CustomTag = defaultProps.tag,
    ...restProps
  } = props;
  return (
    <CustomTag ref={ref} className={styles.subline} {...restProps}>
      {children}
    </CustomTag>
  );
};

export const AccordionSubline = forwardRef(AccordionSublineWithRef);

AccordionSubline.propTypes = {
  tag: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

AccordionSubline.defaultProps = validatedDefaultProps;

AccordionSubline.displayName = "Accordion.Item.Subline";

export default AccordionSubline;
